import { FormActionsMui } from '@app/components/form/FormActionsMui';
import { Page } from '@app/components/page/Page';
import { FragmentIcon } from '@app/modules/fragments/components/FragmentIcon';
import { GetFragmentDto } from '@app/modules/fragments/fragment.types';
import { SOCIAL_EXPORT_FRAGMENT_SOURCE } from '@app/modules/fragments/hooks/useAvailableSocialExportSources';
import { ConnectionProfileDisplay } from '@app/modules/social-export/components/ConnectionProfileDisplay';
import { SocialExportContext } from '@app/modules/social-export/components/dialogs/social-export-context';
import { SocialPlatform } from '@app/modules/social-export/domain/platforms';
import {
  SocialExportMediaSelector,
  SocialExportNoSourcesAlert,
  SocialExportVideoPreview,
  socialExportPublishMessages,
} from '@app/modules/social-export/publish';
import { socialExport, webApi } from '@mi-front/api-clients';
import UploadIcon from '@mui/icons-material/Upload';
import { Box, Grid } from '@mui/material';
import { defineMessage, useIntl } from 'react-intl';

import { type SocialExportPublishForm } from './useSocialExportUploadPage';

const publishLabelMessage = defineMessage({
  id: 'socialExport.form.publish',
  defaultMessage: 'Publicar',
});

export interface SocialExportUploadPageContentProps {
  fragment: GetFragmentDto;
  fragmentMediaUrl: string;
  defaultSettings: socialExport.SettingsGetConnection200;
  platform: SocialPlatform;
  selectedSource: string;
  onSelectedSourceChange: (value: string) => void;
  edits: webApi.EnrichedVideoEditorEditListItemDto[];
  isFragmentSourceAllowed: boolean;
  hasAvailableSources: boolean;
  showMediaSelector: boolean;
  hasSelectedRenderedEdit: boolean;
  renderedVideoUrl: string | null;
  PublishForm: SocialExportPublishForm;
  onUpload: (data: webApi.CreateSocialUploadDto) => Promise<void>;
}

/** Layout and conditional UI once fragment, media, settings, and edits are loaded. */
export function SocialExportUploadPageContent({
  fragment,
  fragmentMediaUrl,
  defaultSettings,
  platform,
  selectedSource,
  onSelectedSourceChange,
  edits,
  isFragmentSourceAllowed,
  hasAvailableSources,
  showMediaSelector,
  hasSelectedRenderedEdit,
  renderedVideoUrl,
  PublishForm,
  onUpload,
}: SocialExportUploadPageContentProps) {
  const { $t } = useIntl();

  const effectiveMediaUrl =
    selectedSource === SOCIAL_EXPORT_FRAGMENT_SOURCE
      ? isFragmentSourceAllowed
        ? (fragmentMediaUrl ?? null)
        : null
      : renderedVideoUrl;

  const canPublish =
    effectiveMediaUrl !== null &&
    ((selectedSource === SOCIAL_EXPORT_FRAGMENT_SOURCE && isFragmentSourceAllowed) ||
      hasSelectedRenderedEdit);

  const title = $t(socialExportPublishMessages.publishToPlatform, { platformName: platform.name });
  const fragmentLabel = fragment.title ?? fragment.id;

  return (
    <SocialExportContext.Provider value={{ mediaUrl: effectiveMediaUrl ?? '' }}>
      <Page htmlTitle={title}>
        <Page.Header
          icon={platform.Icon()}
          title={title}
          breadcrumbs={
            <Page.Breadcrumbs
              items={[
                {
                  icon: <FragmentIcon fontSize="inherit" />,
                  content: fragmentLabel,
                },
              ]}
            />
          }
        />
        <Page.Content maxWidth="lg">
          {!hasAvailableSources ? (
            <SocialExportNoSourcesAlert
              description={socialExportPublishMessages.panoramaNoSourcesFragment}
              linkTo={`/fragments/${fragment.id}/edits`}
            />
          ) : (
            <>
              {showMediaSelector && (
                <Box sx={{ mb: 4, mt: 2 }}>
                  <SocialExportMediaSelector
                    value={selectedSource}
                    onChange={onSelectedSourceChange}
                    edits={edits}
                    includeFragmentOption={isFragmentSourceAllowed}
                  />
                </Box>
              )}
              {!canPublish && selectedSource !== SOCIAL_EXPORT_FRAGMENT_SOURCE
                ? $t(socialExportPublishMessages.mediaSelectorRenderNotReadyFragment)
                : effectiveMediaUrl && (
                    <Grid container spacing={4}>
                      <Grid item xs={12} md={7}>
                        <Box mb={2}>
                          <ConnectionProfileDisplay
                            stationId={fragment.stationId}
                            platform={platform}
                          />
                        </Box>
                        <PublishForm
                          fragment={fragment}
                          videoUrl={effectiveMediaUrl}
                          onUpload={onUpload}
                          defaultSettings={defaultSettings}
                          actions={
                            <FormActionsMui
                              separator
                              saveIcon={<UploadIcon />}
                              saveLabel={$t(publishLabelMessage)}
                              cancel
                            />
                          }
                        />
                      </Grid>
                      <Grid item xs={12} md={5}>
                        <SocialExportVideoPreview videoUrl={effectiveMediaUrl} />
                      </Grid>
                    </Grid>
                  )}
            </>
          )}
        </Page.Content>
      </Page>
    </SocialExportContext.Provider>
  );
}
